import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, useEffect, useState } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import {
  SELECT_FIELD_DEFAULT_VALUE,
  USERNAME_REGEX,
} from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useCheckActions from '../../../hooks/APIHooks/users/useCheckActions';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import usePostalCodesActions from '../../../hooks/APIHooks/users/usePostalCodesActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { editClientSchema } from '../../../schemas';
import { SelectData } from '../../../types/common';
import { Client } from '../../../types/users';
import { showPicture } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import InputFieldBadge from '../Badges/InputFieldBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCountrySelect from '../Inputs/AcademiaCountrySelect';
import AcademiaFileInputField from '../Inputs/AcademiaFileInputField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import BadgeLoading from '../Loadings/BadgeLoading';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const EditClientModal = ({
  handleModalClose,
  client,
}: {
  handleModalClose: () => void;
  client: Client;
}) => {
  // integration of react hooks here
  const [countryCode, setCountryCode] = useState<string>('');
  const [country, setCountry] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [username, setUsername] = useState<string>('');

  const [hasCountryReset, setHasCountryReset] = useState<boolean>(false);
  const [hasProvinceReset, setHasProvinceReset] = useState<boolean>(false);
  const [hasCityReset, setHasCityReset] = useState<boolean>(false);

  const [selectedProvince, setSelectedProvince] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedCity, setSelectedCity] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { updateClientHandler } = useClientActions('');

  const { getCheckAndSuggestUsername } = useCheckActions(username);
  const { getCountryWiseAddedProvincesHandler } = useProvincesActions(
    '',
    undefined,
    country.code
  );
  const { getProvinceWiseCitiesHandler } = useCitiesActions(
    '',
    selectedProvince.id !== -1 ? selectedProvince.id : undefined
  );
  const { getPostalCodesWithCityIdHandler } = usePostalCodesActions(
    '',
    selectedCity.id !== -1 ? selectedCity.id : undefined
  );

  // checking if country has been reselected or not here
  useEffect(() => {
    if (client) {
      if (country.id !== -1 && client.country_name !== country.name) {
        setHasCountryReset(true);
        setSelectedProvince(SELECT_FIELD_DEFAULT_VALUE);
        setSelectedCity(SELECT_FIELD_DEFAULT_VALUE);
      }
    } else {
      if (country) {
        setHasCountryReset(true);
        setSelectedProvince(SELECT_FIELD_DEFAULT_VALUE);
        setSelectedCity(SELECT_FIELD_DEFAULT_VALUE);
      }
    }
  }, [country, client]);

  // setting default value of province & city if client exists here
  useEffect(() => {
    if (client) {
      setSelectedProvince({
        id: client.province_id,
        name: client.province_name,
      });

      setSelectedCity({ id: client.city_id, name: client.city_name });
    }
  }, [client]);

  // handler function to handle create/edit client feature
  const createEditClientHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values, countryCode);

      const body = new FormData();

      // mandatory fields

      // personal details
      body.append('username', values.username.toLowerCase());
      body.append('first_name', values.first_name);
      body.append('last_name', values.last_name);
      body.append('email', values.email);
      body.append('country', country.name);

      // address & phone details
      body.append('phone', values.phone);
      body.append('address', values.address);
      body.append('city', values.city.id);
      body.append('postal_code', values.postal_code.name);

      // optional fields
      values.middle_name && body.append('middle_name', values.middle_name);
      values.profile_picture &&
        body.append('profile_picture', values.profile_picture);

      body.append('updated_by', currentUser?.username as string);

      await updateClientHandler
        .mutateAsync({ clientId: client.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Client Updated!!');

            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['client-list'] });
          } else {
            toast.error(res.message || 'Client Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit client modal component here
  return (
    <ParentModal
      onClick={handleModalClose}
      customWidthClass='md:w-[95%] lg:w-[900px]'
    >
      <ModalTitle titleText='Update Client' />

      <Formik
        initialValues={{
          username: client?.username || '',
          first_name: client?.first_name || '',
          middle_name: client?.middle_name || '',
          last_name: client?.last_name || '',
          email: client?.email || '',
          phone: client?.phone || '',
          country: client?.country_name || '',
          province: client
            ? { id: client.province_id, name: client.province_name }
            : SELECT_FIELD_DEFAULT_VALUE,
          city: client
            ? { id: client.city_id, name: client.city_name }
            : SELECT_FIELD_DEFAULT_VALUE,
          postal_code: client
            ? { id: 0, name: client.postal_code }
            : SELECT_FIELD_DEFAULT_VALUE,
          profile_picture: '',
          address: client?.address || '',
        }}
        validationSchema={editClientSchema}
        onSubmit={createEditClientHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <div>
              <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3 italic'>
                Personal Details
              </h3>

              <div className='grid md:grid-cols-2 gap-4'>
                <div className='space-y-4'>
                  {!client ? (
                    <div>
                      <AcademiaInputField
                        type='text'
                        name='username'
                        label='Username'
                        placeholder='Enter username here...'
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setUsername(e.target.value);
                          props.setFieldValue('username', e.target.value);

                          if (USERNAME_REGEX.test(e.target.value)) {
                            queryClient.invalidateQueries({
                              queryKey: [
                                'check-and-suggest-username',
                                e.target.value,
                              ],
                            });
                          }
                        }}
                        isRequired
                        badge={
                          getCheckAndSuggestUsername.isLoading ? (
                            <BadgeLoading />
                          ) : getCheckAndSuggestUsername.data &&
                            props.values.username ? (
                            <InputFieldBadge
                              badgeText=''
                              badgeValue={
                                getCheckAndSuggestUsername.data.message
                              }
                              isValid={
                                getCheckAndSuggestUsername.data.message ===
                                'Available'
                              }
                            />
                          ) : null
                        }
                      />

                      <AnimatePresence
                        initial={false}
                        mode='wait'
                        onExitComplete={() => null}
                      >
                        {getCheckAndSuggestUsername.data?.data ? (
                          <motion.label
                            initial={{ y: -20, opacity: 0, display: 'none' }}
                            animate={
                              getCheckAndSuggestUsername.data.message ===
                              'Already Taken'
                                ? { y: 0, opacity: 1, display: 'block' }
                                : {
                                    y: -20,
                                    opacity: 0,
                                    transitionEnd: { display: 'none' },
                                  }
                            }
                            htmlFor={`input-user`}
                            className='block mt-1 text-base-red text-sm font-light'
                          >
                            Suggested:
                            {getCheckAndSuggestUsername?.data?.data.map(
                              (suggestion, index) => (
                                <button
                                  key={index}
                                  type='button'
                                  onClick={() => {
                                    setUsername(suggestion);
                                    props.setFieldValue('username', suggestion);
                                  }}
                                  className='font-semibold text-base-cyan  hover:opacity-70 duration-300 mx-1'
                                >
                                  <span className='underline'>
                                    {suggestion}
                                  </span>
                                  {index <
                                  (getCheckAndSuggestUsername?.data?.data
                                    ?.length as number) -
                                    1
                                    ? ', '
                                    : ''}
                                </button>
                              )
                            )}
                            etc.
                          </motion.label>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  ) : null}

                  <AcademiaInputField
                    type='text'
                    name='first_name'
                    label='First Name'
                    placeholder='Enter first name here...'
                    isRequired
                  />

                  <AcademiaInputField
                    type='text'
                    name='middle_name'
                    label='Middle Name'
                    placeholder='Enter middle name here...'
                  />

                  <AcademiaInputField
                    type='text'
                    name='last_name'
                    label='Last Name'
                    placeholder='Enter last name here...'
                    isRequired
                  />
                </div>

                <div className='space-y-4'>
                  <AcademiaInputField
                    type='email'
                    name='email'
                    label='Email'
                    placeholder='Enter email here...'
                    isRequired
                    isDisabled
                  />

                  <AcademiaFileInputField
                    name='profile_picture'
                    label='Profile Picture'
                    btnText='Upload'
                    accept='images/*'
                    placeholder='Upload profile picture here...'
                    filePath={
                      client.profile_picture
                        ? showPicture(client.profile_picture)
                        : ''
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3 italic'>
                Address & Phone Details
              </h3>

              <div className='grid md:grid-cols-2 gap-4'>
                <div className='space-y-4'>
                  <AcademiaCountrySelect
                    name='country'
                    label='Select Country'
                    setCountry={setCountry}
                    defaultCountryCode={client && client.iso_code}
                    isRequired
                  />

                  <AcademiaFormSelect
                    name='province'
                    label='Select Province'
                    options={
                      getCountryWiseAddedProvincesHandler.data?.is_success
                        ? getCountryWiseAddedProvincesHandler.data.data.map(
                            (province) => ({
                              id: province.id,
                              name: province.province_name,
                            })
                          )
                        : []
                    }
                    defaultValue={props.values.province}
                    setValueOnChange={(province) => {
                      setSelectedProvince(province);
                      setHasProvinceReset(true);
                    }}
                    resetSelectField={hasCountryReset}
                    setResetSelectField={() => setHasCountryReset(false)}
                    showSearchInput
                    isDisabled={
                      !getCountryWiseAddedProvincesHandler.data?.is_success
                    }
                    isRequired
                  />

                  <AcademiaFormSelect
                    name='city'
                    label='Select City'
                    options={
                      getProvinceWiseCitiesHandler.data?.is_success
                        ? getProvinceWiseCitiesHandler.data.data.map(
                            (city) => ({
                              id: city.id,
                              name: city.city_name,
                            })
                          )
                        : []
                    }
                    defaultValue={props.values.city}
                    setValueOnChange={(city) => {
                      setSelectedCity(city);
                      setHasCityReset(true);
                    }}
                    resetSelectField={hasCountryReset || hasProvinceReset}
                    setResetSelectField={() => {
                      setHasCountryReset(false);
                      setHasProvinceReset(false);
                    }}
                    showSearchInput
                    isDisabled={!getProvinceWiseCitiesHandler.data?.is_success}
                    isRequired
                  />

                  <AcademiaFormSelect
                    name='postal_code'
                    label='Select Postal Code'
                    options={
                      getPostalCodesWithCityIdHandler.data?.is_success
                        ? getPostalCodesWithCityIdHandler.data.data[0].postal_codes.map(
                            (postalCode, index) => ({
                              id: index,
                              name: postalCode,
                            })
                          )
                        : []
                    }
                    defaultValue={props.values.postal_code}
                    resetSelectField={
                      hasCountryReset || hasProvinceReset || hasCityReset
                    }
                    setResetSelectField={() => {
                      setHasCountryReset(false);
                      setHasProvinceReset(false);
                      setHasCityReset(false);
                    }}
                    showSearchInput
                    isDisabled={
                      !getPostalCodesWithCityIdHandler.data?.is_success
                    }
                    isRequired
                  />
                </div>

                <div className='space-y-4'>
                  <AcademiaInputField
                    type='number'
                    name='phone'
                    label='Phone Number'
                    placeholder='Enter phone number here...'
                    setCountryCode={setCountryCode}
                    defaultSelectedCountryCode={country.code}
                    isRequired
                  />

                  <AcademiaTextAreaField
                    name='address'
                    label='Address'
                    placeholder={`Enter user's address here...`}
                    isRequired
                  />
                </div>
              </div>
            </div>

            <div className='w-full md:w-3/5 mx-auto'>
              <AcademiaButton
                title={
                  props.values.province.id === -1
                    ? 'Please, select a province first!'
                    : props.values.city.id === -1
                    ? 'Please, select a city first!'
                    : props.values.postal_code.id === -1
                    ? 'Please, select a postal code first!'
                    : ''
                }
                type='submit'
                variant='premium'
                isLoading={props.isSubmitting}
                isDisabled={
                  props.values.province.id === -1 ||
                  props.values.city.id === -1 ||
                  props.values.postal_code.id === -1
                }
                icon={<RxUpdate className='size-6' />}
                fullWidth
              >
                Update Client
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default EditClientModal;
